import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * A single IntersectionObserver drives every `.anim-reveal` element on the
 * page. Elements just add the class (via the `reveal()` helper) and the
 * provider flips them to `is-visible` as they scroll into view — no per-item
 * hooks, so it works inside `.map()` and on any MUI component.
 *
 * Re-scans on route change so newly mounted pages animate too.
 */
export default function RevealProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { pathname } = useLocation();

  useEffect(() => {
    const nodes = Array.from(
      document.querySelectorAll<HTMLElement>(".anim-reveal:not(.is-visible)")
    );
    if (nodes.length === 0) return;

    const prefersReduced =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (prefersReduced || typeof IntersectionObserver === "undefined") {
      nodes.forEach((node) => node.classList.add("is-visible"));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );

    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [pathname]);

  return <>{children}</>;
}
